import {getAppOrigin} from '@/utils/urlUtils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import ConstructorIOClient, {
    IBrowseParameters,
    RecommendationsResponse,
    SearchResponse,
    BrowseResponse
} from '@constructor-io/constructorio-client-javascript'
import {isClient} from '@/utils/common'

let cioClient: ConstructorIOClient | null = null
let cioClientPromise: Promise<ConstructorIOClient | null> | null = null

const getCioClient = (origin: string): Promise<ConstructorIOClient | null> => {
    const config = getConfig()
    if (cioClient) {
        return Promise.resolve(cioClient)
    }
    if (!cioClientPromise) {
        cioClientPromise = new Promise((resolve) => {
            if (isClient) {
                cioClient = new ConstructorIOClient({
                    apiKey: config.app.constructor.apiKey,
                    sendTrackingEvents: false,
                    serviceUrl: `${origin}/api/constructor/`
                })
                resolve(cioClient)
            } else {
                resolve(null) // Handle server-side case
            }
        })
    }

    return cioClientPromise
}

export const getCioCategories = async (
    origin: string,
    categoryId: string,
    options?: IBrowseParameters
): Promise<BrowseResponse<Record<string, undefined>> | null> => {
    try {
        const client = await getCioClient(origin)
        if (!client) {
            console.warn('cioClient is not available on the server.')
            return null
        }
        if (!categoryId) {
            console.error('fetchConstructorCategories: categoryId parameter is required')
            return null
        }
        return await client.browse.getBrowseResults('group_id', categoryId, options)
    } catch (error) {
        console.error('Error fetching Constructor.io categories:', error)
        return null
    }
}

export const getCioSearch = async (
    origin: string,
    query: string,
    options: IBrowseParameters
): Promise<SearchResponse | null> => {
    try {
        const client = await getCioClient(origin)
        if (!client) {
            console.warn('cioClient is not available on the server.')
            return null
        }

        if (!query) {
            console.error('fetchConstructorCategories: query parameter is required')
            return null
        }

        return await client.search.getSearchResults(query, options)
    } catch (error) {
        console.error('Error fetching Constructor.io search:', error)
        return null
    }
}

interface GetCioRecommendationsProps {
    podId: string
    productIds?: string[]
    groupId?: string
}

interface Filters {
    group_id?: string
}
interface GetCioRecommendationsConfig {
    numResults: number
    itemIds?: string[]
    filters?: {
        group_id: string | undefined
    }
}

export const getCioRecommendations = async (
    origin: string,
    {podId, productIds, groupId}: GetCioRecommendationsProps
): Promise<RecommendationsResponse | null> => {
    try {
        const cioClient = await getCioClient(origin)
        if (!cioClient) {
            console.error('cioClient is not initialized.')
            return null
        }

        const recommendationsConfig: GetCioRecommendationsConfig = {
            numResults: 10,
            itemIds: productIds,
            ...(groupId ? {filters: {group_id: groupId}} : {})
        }

        const response = await cioClient.recommendations.getRecommendations(
            podId,
            recommendationsConfig
        )

        if (!response) {
            console.warn('No recommendations received.')
            return null
        }

        return response
    } catch (error) {
        console.error('Error fetching product recommendations:', error)
        return null
    }
}

interface ProductData {
    id: string
    [key: string]: any
}

interface PriceData {
    id: string
    [key: string]: any
}

export const mergeCioProductDataWithPrices = (
    productData: {pages: {results: ProductData[]}[]} | null,
    priceData: PriceData[] | null
): {pages: {results: ProductData[]}[]} | null => {
    if (!productData || !priceData) return productData // Return original if no prices available

    return {
        pages: productData.pages.map((page) => ({
            ...page,
            results: page.results.map((product) => ({
                ...product,
                price: priceData.find((price) => price.id === product.id) || {} // Merge price
            }))
        }))
    }
}
